import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useMatch } from 'react-router';
import { Card } from '../../../../design-system';
import Alert from '../../../../helpers/alert';
import { successRefetch } from '../../../../helpers/data-loader';
import { findItemViaPathname } from '../../../../helpers/utils';
import AttachmentsBase from '../../../../components/Attachments';
import ModalForm from '../../../../components/ModalForm';
import {
  Table,
  TableCardHeader,
  TableBody as TableBodyBase,
} from '../../../../components/Table';
import SingleEntity from '../../../../components/SingleEntity';
import { COLUMNS, MODAL_FIELDS, SINGLE_ENTITY_FIELDS } from './constants';
import { ERR_MSG } from '../../../../globalConstants';

const Attachments = AttachmentsBase as React.ComponentType<any>;
const TableBody = TableBodyBase as React.ComponentType<any>;

interface NotesVerbiageProps {
  data: { id: any; verbiage: any[]; all_files: any[]; updated_at?: any };
  pathname: string;
  navigate?: any;
  refetch: () => void;
  canEdit: boolean;
}

const NotesVerbiage = ({
  data: { id, verbiage, all_files, updated_at: updatedAt },
  pathname,
  refetch,
  canEdit,
}: NotesVerbiageProps) => {
  const navigate = useNavigate();
  const [isModalOpen, toggleModal] = useState(false);
  const [isAttachmentsModalOpen, toggleAttachmentsModal] = useState(false);
  const selectedVerbiage = findItemViaPathname(verbiage, pathname);
  const onVerbiageRoute = useMatch(`/vault/${id}/notes-verbiage/:id`);

  useEffect(() => {
    if (onVerbiageRoute && !selectedVerbiage) {
      navigate(-1);
    }
  }, [onVerbiageRoute, selectedVerbiage, navigate]);

  const handleVerbiageCreate = async (values) => {
    try {
      await axios.post(`/client/${id}/verbiage`, values);
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };
  const handleVerbiageUpdate = async (values) => {
    try {
      await axios.put(`/client/${id}/verbiage/${selectedVerbiage.id}`, values);
      successRefetch(refetch);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };
  const handleDeleteVerbiage = async (item) => {
    try {
      await axios.delete(`/client/${id}/verbiage/${item.id}`);
      successRefetch(refetch);
      navigate(`/vault/${id}/notes-verbiage`);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };
  return (
    <>
      <Card id="Verbiage-table">
        <TableCardHeader
          name="Notes/Verbiage"
          action="Add"
          onClick={() => toggleModal(true)}
          disabled={!canEdit}
          updatedAt={updatedAt}
        />
        <TableBody maxHeight="22rem">
          <Table
            data={verbiage.map((item) => ({
              ...item,
              handleDeleteVerbiage,
              canEdit,
            }))}
            columns={COLUMNS}
          />
        </TableBody>
        <ModalForm
          title="Add Note/Verbiage"
          isOpen={isModalOpen}
          onSubmit={handleVerbiageCreate}
          onClose={() => toggleModal(false)}
          fields={MODAL_FIELDS}
          oneColumn
        />
      </Card>
      {onVerbiageRoute &&
        (!selectedVerbiage ? null : (
          <>
            <SingleEntity
              data={selectedVerbiage}
              fields={SINGLE_ENTITY_FIELDS}
              onSubmit={handleVerbiageUpdate}
              onClose={() => navigate(`/vault/${id}/notes-verbiage`)}
              canEdit={canEdit}
            />
            <Attachments
              data={all_files.filter(
                ({ model_type, model_id }) =>
                  model_type === 'VaultVerbiage' &&
                  model_id === selectedVerbiage.id,
              )}
              canEdit={canEdit}
              isModalOpen={isAttachmentsModalOpen}
              onClick={() => toggleAttachmentsModal(true)}
              onClose={() => toggleAttachmentsModal(false)}
              modelId={selectedVerbiage.id}
              modelType="VaultVerbiage"
              clientId={id}
              refetch={refetch}
              maxHeight="15rem"
              disabled={!canEdit}
            />
          </>
        ))}
    </>
  );
};

export default NotesVerbiage;
